import React, { useState, useEffect } from 'react';

// Reuse your existing PageStyles (Make sure this is included in your app)
const PageStyles = () => (
  <style>{`
    :root { scroll-behavior: smooth; }
    body {
        font-family: 'Inter', sans-serif;
        background-color: #050505;
        color: #e5e7eb;
        line-height: 1.6;
        overflow-x: hidden;
    }
    @keyframes fadeInUp {
        from { opacity: 0; transform: translateY(30px); }
        to { opacity: 1; transform: translateY(0); }
    }
    .animate-on-scroll { opacity: 0; }
    .animate-visible { animation: fadeInUp 0.8s cubic-bezier(0.2, 0.8, 0.2, 1) forwards; }
    .delay-100 { animation-delay: 0.1s; }
    .delay-200 { animation-delay: 0.2s; }
    .delay-300 { animation-delay: 0.3s; }

    .hero-bg {
        background-color: #050505;
        background-image: 
            radial-gradient(circle at 15% 50%, rgba(56, 189, 248, 0.08) 0%, transparent 40%),
            radial-gradient(circle at 85% 30%, rgba(129, 140, 248, 0.08) 0%, transparent 40%),
            linear-gradient(#0a0a0a 1px, transparent 1px),
            linear-gradient(90deg, #0a0a0a 1px, transparent 1px);
        background-size: 100% 100%, 100% 100%, 40px 40px, 40px 40px;
    }
    .gradient-text {
        background: linear-gradient(135deg, #38bdf8, #818cf8, #c084fc);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
    }
    .btn-primary {
        background: linear-gradient(135deg, #0ea5e9, #4f46e5);
        position: relative;
        overflow: hidden;
        transition: all 0.3s ease;
        box-shadow: 0 4px 20px rgba(14, 165, 233, 0.3);
    }
    .btn-primary:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 30px rgba(14, 165, 233, 0.5);
    }
    .btn-secondary {
        background: rgba(255, 255, 255, 0.03);
        border: 1px solid rgba(255, 255, 255, 0.1);
        backdrop-filter: blur(10px);
        transition: all 0.3s ease;
    }
    .btn-secondary:hover {
        background: rgba(255, 255, 255, 0.08);
        border-color: rgba(14, 165, 233, 0.5);
    }
    .feature-card {
        background: linear-gradient(180deg, rgba(30, 41, 59, 0.2), rgba(15, 23, 42, 0.4));
        border: 1px solid rgba(255, 255, 255, 0.05);
        backdrop-filter: blur(12px);
        transition: all 0.4s ease;
    }
    .feature-card:hover {
        transform: translateY(-5px);
        border-color: rgba(56, 189, 248, 0.3);
        box-shadow: 0 10px 40px rgba(0, 0, 0, 0.4);
    }
  `}</style>
);

const plansData = [
  {
    name: "Basic Plan",
    priceUSD: 10,
    priceINR: 799,
    mcq_credits: 30,
    validity: "Lifetime",
    popular: false,
    features: [
      "MCQ automation",
      "Real-time screen analysis",
      "AI-based decision making",
      "Human-like auto mouse movement"
    ],
    missingFeatures: [
      "Neural driver link",
      "Process hide & Auto restart",
      "Remote service",
      "Stealth network features"
    ],
    remote: { included: false }
  },
  {
    name: "Pro Plan",
    priceUSD: 18,
    priceINR: 1499,
    mcq_credits: 60,
    validity: "Lifetime",
    popular: true,
    features: [
      "MCQ automation",
      "Real-time screen analysis",
      "AI-based decision making",
      "Human-like auto mouse movement",
      "Neural driver link",
      "Process hide name",
      "Auto termination protection",
      "Auto restart if software is killed"
    ],
    missingFeatures: [
      "Network bypass (8.8.8.8 / 1.1.1.1)",
      "Anti-fingerprint detection"
    ],
    remote: {
      included: true,
      duration: "3 Days",
      activation: "Starts from first connection",
      extendable: false
    }
  },
  {
    name: "Premium Plus",
    priceUSD: 25,
    priceINR: 1999,
    mcq_credits: 100,
    validity: "Lifetime",
    popular: false,
    features: [
      "MCQ automation",
      "Real-time screen analysis",
      "AI-based decision making",
      "Human-like auto mouse movement",
      "Neural driver link",
      "Process hide name",
      "Auto termination protection",
      "Auto restart if software is killed",
      "Network bypass (8.8.8.8 / 1.1.1.1)",
      "Anti-fingerprint detection",
      "Stealth network features"
    ],
    missingFeatures: [],
    remote: {
      included: true,
      duration: "7 Days",
      activation: "Starts from first connection",
      extendable: true,
      extension_method: "Contact support for extension"
    }
  }
];

const CheckScriptPlans = () => {
  const [isIndianUser, setIsIndianUser] = useState(false);

  // Reusing your scroll animation logic
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                const delay = parseInt(entry.target.dataset.delay || 0, 10);
                entry.target.style.animationDelay = `${delay}ms`;
                entry.target.classList.add('animate-visible');
            }
        });
    }, { threshold: 0.1, rootMargin: "0px 0px -50px 0px" });

    document.querySelectorAll('.animate-on-scroll').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <PageStyles />
      
      <section className="hero-bg min-h-screen py-24 relative overflow-hidden">
        <div className="container mx-auto px-4 max-w-7xl relative z-10">
          
          {/* Header */}
          <div className="text-center mb-12 animate-on-scroll" data-delay="100">
            <span className="inline-block py-1 px-3 rounded-full bg-indigo-900/30 border border-indigo-500/30 text-indigo-300 text-xs font-bold tracking-wider mb-4">
              PRICING & PLANS
            </span>
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6">
              Choose your <span className="gradient-text">God Mode</span>.
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Unlock the full potential of CheckScript v13. MCQ credits never expire, and all plans include our core AI engine.
            </p>
          </div>

          {/* Pricing Toggle */}
          <div className="flex justify-center mb-16 animate-on-scroll" data-delay="200">
            <div className="bg-gray-900/80 backdrop-blur-md p-1.5 rounded-2xl border border-gray-800 flex items-center shadow-2xl">
              <button 
                onClick={() => setIsIndianUser(false)}
                className={`px-6 py-2.5 rounded-xl text-sm font-semibold transition-all ${!isIndianUser ? 'bg-indigo-600 text-white shadow-lg' : 'text-gray-400 hover:text-white'}`}
              >
                🌍 International (USD)
              </button>
              <button 
                onClick={() => setIsIndianUser(true)}
                className={`px-6 py-2.5 rounded-xl text-sm font-semibold transition-all ${isIndianUser ? 'bg-indigo-600 text-white shadow-lg' : 'text-gray-400 hover:text-white'}`}
              >
                🇮🇳 Indian Users (INR)
              </button>
            </div>
          </div>

          {/* Plans Grid */}
          <div className="grid lg:grid-cols-3 gap-8 items-stretch">
            {plansData.map((plan, index) => (
              <div 
                key={plan.name} 
                className={`feature-card rounded-3xl p-8 flex flex-col relative animate-on-scroll ${plan.popular ? 'border-indigo-500/50 scale-105 shadow-2xl shadow-indigo-500/10' : ''}`}
                data-delay={(index + 1) * 100}
              >
                {/* Popular Badge */}
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-sky-500 to-indigo-500 text-white text-[10px] font-bold tracking-widest px-4 py-1.5 rounded-full uppercase shadow-lg">
                    Most Popular
                  </div>
                )}

                {/* Plan Header */}
                <div className="mb-8">
                  <h3 className="text-2xl font-bold text-white mb-2">{plan.name}</h3>
                  <div className="flex items-baseline gap-2 mb-4">
                    <span className="text-4xl font-black text-white">
                      {isIndianUser ? `₹${plan.priceINR}` : `$${plan.priceUSD}`}
                    </span>
                    <span className="text-gray-500 text-sm">/ {plan.mcq_credits} Credits</span>
                  </div>
                  <p className="text-sky-400 text-sm font-medium bg-sky-500/10 inline-block px-3 py-1 rounded-lg border border-sky-500/20">
                    Validity: {plan.validity}
                  </p>
                </div>

                {/* Feature List */}
                <div className="space-y-4 mb-8 flex-grow">
                  {plan.features.map((feature, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-emerald-400 text-xs">✓</span>
                      </div>
                      <span className="text-gray-300 text-sm">{feature}</span>
                    </div>
                  ))}
                  
                  {/* Remote Service Specifics */}
                  {plan.remote.included ? (
                    <div className="flex items-start gap-3 mt-6 p-4 rounded-xl bg-indigo-500/10 border border-indigo-500/20">
                      <div className="text-indigo-400 mt-0.5">🖥️</div>
                      <div>
                        <span className="text-indigo-300 text-sm font-semibold block">Remote Access Included</span>
                        <span className="text-gray-400 text-xs block mt-1">Duration: {plan.remote.duration}</span>
                        {plan.remote.extendable && (
                          <span className="text-indigo-400/80 text-[10px] uppercase font-bold tracking-wider mt-2 block">Extendable via Support</span>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-gray-600 text-xs">✗</span>
                      </div>
                      <span className="text-gray-600 text-sm line-through">Remote service included</span>
                    </div>
                  )}

                  {/* Missing Features */}
                  {plan.missingFeatures.map((feature, i) => (
                    <div key={`missing-${i}`} className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-gray-600 text-xs">✗</span>
                      </div>
                      <span className="text-gray-600 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>

                {/* CTA Button */}
                <button className={`w-full py-4 rounded-xl font-bold text-sm transition-all ${plan.popular ? 'btn-primary text-white' : 'btn-secondary text-gray-300'}`}>
                  Get {plan.name}
                </button>
              </div>
            ))}
          </div>

          {/* Notes Section */}
          <div className="mt-20 border-t border-gray-800/50 pt-10 animate-on-scroll" data-delay="400">
            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              <div className="bg-gray-900/50 rounded-2xl p-6 border border-gray-800">
                <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                  <span className="text-amber-500">💡</span> Important Notes
                </h4>
                <ul className="space-y-2 text-sm text-gray-400 list-disc list-inside">
                  <li>MCQ credits are lifetime (no expiry).</li>
                  <li>Human-like auto mouse is included natively in all plans.</li>
                  <li>Remote extension is exclusively available for Premium Plus users via contact.</li>
                </ul>
              </div>
              <div className="bg-gray-900/50 rounded-2xl p-6 border border-gray-800">
                <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                  <span className="text-sky-500">⏱️</span> Remote Service Activation
                </h4>
                <p className="text-sm text-gray-400">
                  Your remote service duration (3 Days for Pro, 7 Days for Premium Plus) automatically starts from your <strong>very first connection</strong>, not from the time of purchase. Use it when you need it.
                </p>
              </div>
            </div>
          </div>

        </div>
      </section>
    </>
  );
};

export default CheckScriptPlans;